import type { ApiClient } from './ApiClient';
import { createApiClient } from './ApiClient';
import { createAxiosInstance, type HttpBreadcrumbCallback } from './createAxiosInstance';

/**
 * Bootstrap helper for creating a fully configured ApiClient
 *
 * This is a convenience function that wires together:
 * - Axios instance with auth interceptors
 * - ApiClient with dependency injection
 * - Auth token providers (passed in via options)
 *
 * Note: Apps must provide token provider functions (typically from @commander/auth package)
 *
 * @param options - Configuration options (all required for proper auth)
 * @param options.baseURL - API base URL (defaults to VITE_API_BASE_URL env var)
 * @param options.getToken - Token retrieval function (required for auth)
 * @param options.onTokenExpired - Token expired handler (required)
 * @param options.onUnauthorized - Unauthorized handler (required)
 * @param options.onHttpBreadcrumb - Optional callback for HTTP breadcrumbs (for observability)
 * @returns Configured ApiClient instance ready to use
 *
 * @example
 * ```typescript
 * // In app's main.tsx
 * import { bootstrapApiClient } from '@commander/api-client';
 * import { getValidatedAuthToken, handleTokenExpired, handleUnauthorized } from '@commander/auth';
 * import { addHttpBreadcrumb } from '@commander/observability';
 *
 * const apiClient = bootstrapApiClient({
 *   baseURL: import.meta.env.VITE_API_BASE_URL,
 *   getToken: getValidatedAuthToken,
 *   onTokenExpired: handleTokenExpired,
 *   onUnauthorized: handleUnauthorized,
 *   onHttpBreadcrumb: addHttpBreadcrumb, // Optional: enables request tracking
 * });
 *
 * // Use with React Context
 * <ApiClientProvider client={apiClient}>
 *   <App />
 * </ApiClientProvider>
 * ```
 */
export function bootstrapApiClient(options: {
  baseURL?: string;
  getToken: () => string | null;
  onTokenExpired: () => void;
  onUnauthorized: () => Promise<void>;
  onHttpBreadcrumb?: HttpBreadcrumbCallback;
}): ApiClient {
  const axiosInstance = createAxiosInstance({
    baseURL: options.baseURL,
    getToken: options.getToken,
    onTokenExpired: options.onTokenExpired,
    onUnauthorized: options.onUnauthorized,
    onHttpBreadcrumb: options.onHttpBreadcrumb,
  });

  return createApiClient(axiosInstance);
}
